/**
 * File System Access API for storing study locally
 * Allows storing large files outside IndexedDB to avoid quota limits
 * Requires user permission to access a directory
 */

import JSZip from 'jszip';

class FileSystemStorage {
  constructor() {
    this.directoryHandle = null;
    this.isSupported = 'showDirectoryPicker' in window;
  }

  /**
   * Check if File System Access API is supported
   */
  isFileSystemAccessSupported() {
    return this.isSupported;
  }

  /**
   * Request directory access from user
   * @returns {Promise<boolean>} True if access granted
   */
  async requestDirectoryAccess() {
    if (!this.isSupported) {
      throw new Error(
        'File System Access API not supported in this browser, please use Chrome, Edge or Opera'
      );
    }

    try {
      // Request directory picker
      this.directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'downloads',
      });

      // Store handle reference in IndexedDB for persistence
      await this.storeDirectoryHandle();

      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(
          '[FileSystemStorage] Error requesting directory access:',
          error
        );
      }
      return false;
    }
  }

  /**
   * Store directory handle in IndexedDB for persistence across sessions
   */
  async storeDirectoryHandle() {
    if (!this.directoryHandle) {
      console.log('[FileSystemStorage] ❌ No directory handle to store');
      return;
    }

    try {
      const db = await this.openDB();
      const tx = db.transaction('directory_handles', 'readwrite');
      const store = tx.objectStore('directory_handles');

      const recordToStore = {
        id: 'main_directory',
        handle: this.directoryHandle,
        timestamp: Date.now(),
      };

      // Wrap the put operation in a Promise
      await new Promise((resolve, reject) => {
        const request = store.put(recordToStore);
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => {
          console.error(
            '[FileSystemStorage] ❌ Put operation failed:',
            request.error
          );
          reject(request.error);
        };
      });

      // Wait for transaction to complete
      await new Promise((resolve, reject) => {
        tx.oncomplete = () => {
          resolve();
        };
        tx.onerror = () => {
          console.error('[FileSystemStorage] ❌ Transaction failed:', tx.error);
          reject(tx.error);
        };
      });
    } catch (error) {
      console.error(
        '[FileSystemStorage] ❌ Error storing directory handle:',
        error
      );
    }
  }

  /**
   * Restore directory handle from IndexedDB
   */
  async restoreDirectoryHandle() {
    try {
      const db = await this.openDB();
      const tx = db.transaction('directory_handles', 'readonly');
      const store = tx.objectStore('directory_handles');

      // Wrap the get operation in a Promise
      const record = await new Promise((resolve, reject) => {
        const request = store.get('main_directory');
        request.onsuccess = () => {
          resolve(request.result);
        };
        request.onerror = () => {
          console.error(
            '[FileSystemStorage] ❌ Get operation failed:',
            request.error
          );
          reject(request.error);
        };
      });

      if (record && record.handle) {
        this.directoryHandle = record.handle;

        // Verify we still have permission
        const permission = await this.directoryHandle.queryPermission({
          mode: 'readwrite',
        });

        if (permission !== 'granted') {
          // Can't request permission without user gesture
          // User needs to click the button to re-grant access
          this.directoryHandle = null;
          return false;
        }

        return true;
      }
    } catch (error) {
      console.error(
        '[FileSystemStorage] ❌ Error restoring directory handle:',
        error
      );
    }

    return false;
  }

  /**
   * Open IndexedDB for storing directory handles
   */
  async openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('azviewer_filesystem', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('directory_handles')) {
          db.createObjectStore('directory_handles', { keyPath: 'id' });
        }
      };
    });
  }

  /**
   * Get or create study directory
   * @param {string} studyUID - Study UID
   * @returns {Promise<FileSystemDirectoryHandle>}
   */
  async getOrCreateStudyDirectory(studyUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        throw new Error(
          'No directory access. Call requestDirectoryAccess() first.'
        );
      }
    }

    // Create cache root directory
    const cacheDir = await this.directoryHandle.getDirectoryHandle(
      'azviewer_cache',
      {
        create: true,
      }
    );

    // Create studies subdirectory
    const studiesDir = await cacheDir.getDirectoryHandle('studies', {
      create: true,
    });

    // Create directory for this study
    const studyDir = await studiesDir.getDirectoryHandle(studyUID, {
      create: true,
    });

    return studyDir;
  }

  /**
   * Parse imageId to extract UIDs
   * @param {string} imageId - Format: wadors:https://.../studies/{study}/series/{series}/instances/{sop}/frames/{frame}
   * @returns {Object} { studyUID, seriesUID, sopUID, frameNum }
   */
  parseImageId(imageId) {
    const match = imageId.match(
      /studies\/([^\/]+)\/series\/([^\/]+)\/instances\/([^\/]+)\/frames\/(\d+)/
    );

    if (!match) {
      throw new Error(`Invalid imageId format: ${imageId}`);
    }

    return {
      studyUID: match[1],
      seriesUID: match[2],
      sopUID: match[3],
      frameNum: parseInt(match[4], 10),
    };
  }

  /**
   * Save a single DICOM image to File System
   * @param {string} imageId - Full imageId (wadors:https://...)
   * @param {ArrayBuffer} arrayBuffer - Raw pixel data
   * @param {Object} metadata - { studyUID, size }
   */
  async saveImage(imageId, arrayBuffer, metadata) {
    const { studyUID, seriesUID, sopUID, frameNum } = this.parseImageId(
      imageId
    );

    // Create directory structure
    const studyDir = await this.getOrCreateStudyDirectory(studyUID);
    const imagesDir = await studyDir.getDirectoryHandle('images', {
      create: true,
    });
    const seriesDir = await imagesDir.getDirectoryHandle(seriesUID, {
      create: true,
    });

    // Save image file
    const fileName = `${sopUID}_frame${frameNum}.bin`;
    const fileHandle = await seriesDir.getFileHandle(fileName, {
      create: true,
    });
    const writable = await fileHandle.createWritable();
    await writable.write(arrayBuffer);
    await writable.close();
  }

  /**
   * Load a single DICOM image from File System
   * @param {string} imageId - Full imageId
   * @returns {ArrayBuffer} Raw pixel data or null if not cached
   */
  async loadImage(imageId) {
    const { studyUID, seriesUID, sopUID, frameNum } = this.parseImageId(
      imageId
    );

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(studyUID);
      const imagesDir = await studyDir.getDirectoryHandle('images');
      const seriesDir = await imagesDir.getDirectoryHandle(seriesUID);
      const fileHandle = await seriesDir.getFileHandle(
        `${sopUID}_frame${frameNum}.bin`
      );
      const file = await fileHandle.getFile();
      return await file.arrayBuffer();
    } catch (error) {
      return null; // Not cached
    }
  }

  /**
   * Check if an image is cached
   * @param {string} imageId
   * @returns {boolean}
   */
  async hasImage(imageId) {
    const data = await this.loadImage(imageId);
    return data !== null;
  }

  /**
   * Save study ZIP to file system (DEPRECATED - kept for compatibility)
   * @param {string} StudyInstanceUID - Study UID
   * @param {Blob} zipBlob - ZIP file blob
   * @param {Object} metadata - Study metadata
   * @returns {Promise<boolean>} Success status
   */
  async saveStudy(StudyInstanceUID, zipBlob, metadata) {
    if (!this.directoryHandle) {
      // Try to restore handle
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        throw new Error(
          'No directory access. Call requestDirectoryAccess() first.'
        );
      }
    }

    try {
      // Create cache root directory
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache',
        {
          create: true,
        }
      );

      // Create studies subdirectory
      const studiesDir = await cacheDir.getDirectoryHandle('studies', {
        create: true,
      });

      // Create directory for this study
      const studyDir = await studiesDir.getDirectoryHandle(StudyInstanceUID, {
        create: true,
      });

      // Save ZIP file
      const zipFile = await studyDir.getFileHandle('study.zip', {
        create: true,
      });
      const zipWritable = await zipFile.createWritable();
      await zipWritable.write(zipBlob);
      await zipWritable.close();

      // Save metadata as JSON
      const metadataFile = await studyDir.getFileHandle('metadata.json', {
        create: true,
      });
      const metadataWritable = await metadataFile.createWritable();
      await metadataWritable.write(
        new Blob([JSON.stringify(metadata, null, 2)], {
          type: 'application/json',
        })
      );
      await metadataWritable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving study:', error);
      throw error;
    }
  }

  /**
   * Load study ZIP from file system (DEPRECATED - kept for compatibility)
   * @param {string} StudyInstanceUID - Study UID
   * @returns {Promise<Object>} { zipBlob, metadata }
   */
  async loadStudy(StudyInstanceUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(StudyInstanceUID);

      // Load ZIP file
      const zipFile = await studyDir.getFileHandle('study.zip');
      const zipBlob = await zipFile.getFile();

      // Load metadata
      const metadataFile = await studyDir.getFileHandle('metadata.json');
      const metadataBlob = await metadataFile.getFile();
      const metadataText = await metadataBlob.text();
      const metadata = JSON.parse(metadataText);

      return { zipBlob, metadata };
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error('[FileSystemStorage] Error loading study:', error);
      throw error;
    }
  }

  /**
   * Check if study exists in file system (checks both old and new locations)
   * @param {string} StudyInstanceUID - Study UID
   * @returns {Promise<boolean>}
   */
  async hasStudy(StudyInstanceUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      await studiesDir.getDirectoryHandle(StudyInstanceUID);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Delete study from file system
   * @param {string} StudyInstanceUID - Study UID
   * @returns {Promise<boolean>}
   */
  async deleteStudy(StudyInstanceUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      await studiesDir.removeEntry(StudyInstanceUID, { recursive: true });
      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error deleting study:', error);
      return false;
    }
  }

  /**
   * List all studies in file system
   * @returns {Promise<Array<string>>} Array of StudyInstanceUIDs
   */
  async listStudies() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return [];
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studies = [];

      for await (const entry of studiesDir.values()) {
        if (entry.kind === 'directory') {
          studies.push(entry.name);
        }
      }

      return studies;
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return [];
      }
      return [];
    }
  }

  /**
   * Save a thumbnail image to File System
   * @param {string} studyUID - Study UID
   * @param {string} seriesUID - Series UID (optional, for composite key)
   * @param {Blob} thumbnailBlob - Thumbnail image blob
   * @returns {Promise<boolean>} Success status
   */
  async saveThumbnail(instanceID, thumbnailBlob) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache',
        { create: true }
      );
      const thumbnailsDir = await cacheDir.getDirectoryHandle('thumbnails', {
        create: true,
      });

      // Use composite key if seriesUID provided, otherwise just studyUID
      const fileName = `${instanceID}.jpg`;

      const fileHandle = await thumbnailsDir.getFileHandle(fileName, {
        create: true,
      });
      const writable = await fileHandle.createWritable();
      await writable.write(thumbnailBlob);
      await writable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving thumbnail:', error);
      return false;
    }
  }

  /**
   * Load a thumbnail image from File System
   * @param {string} folder - Folder name
   * @returns {Promise<Blob|null>} Thumbnail blob or null if not found
   */
  async getThumbnail(folder) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'orthanc_duplicata'
      );
      const thumbnailsDir = await cacheDir.getDirectoryHandle(folder);
      const fileHandle = await thumbnailsDir.getFileHandle(`preview.jpg`);
      return await fileHandle.getFile();
    } catch (error) {
      return null; // Not cached
    }
  }

  /**
   * Get complete study metadata from File System
   * @param {string} studyUID - Study UID
   * @returns {Promise<Object|null>} Study metadata or null if not found
   */
  async getStudyMetadata(studyUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(studyUID);
      const metadataFile = await studyDir.getFileHandle('metadata.json');
      const file = await metadataFile.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch (error) {
      return null; // Not cached
    }
  }

  /**
   * Save complete study metadata to File System
   * @param {string} studyUID - Study UID
   * @param {Object} metadata - Complete study metadata
   * @returns {Promise<boolean>} Success status
   */
  async saveStudyMetadata(studyUID, metadata) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const studyDir = await this.getOrCreateStudyDirectory(studyUID);
      const metadataFile = await studyDir.getFileHandle('metadata.json', {
        create: true,
      });
      const writable = await metadataFile.createWritable();
      await writable.write(
        new Blob([JSON.stringify(metadata, null, 2)], {
          type: 'application/json',
        })
      );
      await writable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving metadata:', error);
      return false;
    }
  }

  /**
   * Get detailed information about cached studies
   * @returns {Promise<Array>} Array of study metadata objects
   */
  async getCachedStudies() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return [];
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studies = [];

      for await (const entry of studiesDir.values()) {
        if (entry.kind === 'directory') {
          try {
            // Read metadata file
            const studyDir = await studiesDir.getDirectoryHandle(entry.name);
            const metadataFile = await studyDir.getFileHandle('metadata.json');
            const file = await metadataFile.getFile();
            const text = await file.text();
            const metadata = JSON.parse(text);

            studies.push({
              studyUID: entry.name,
              cachedDate:
                metadata.cachedDate || metadata.timestamp || Date.now(),
              size: metadata.size || 0,
            });
          } catch (err) {
            // If metadata file doesn't exist, just include the study UID
            studies.push({
              studyUID: entry.name,
              cachedDate: null,
              size: 0,
            });
          }
        }
      }

      return studies;
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return [];
      }
      console.error(
        '[FileSystemStorage] ❌ Error getting cached studies:',
        error
      );
      return [];
    }
  }

  /**
   * Check if a study has all its images downloaded
   * @param {string} studyUID - Study UID
   * @returns {Promise<Object>} { isComplete: boolean, expected: number, actual: number }
   */
  async checkStudyIntegrity(studyUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return { isComplete: false, expected: 0, actual: 0 };
      }
    }

    try {
      // Get study metadata
      const metadata = await this.getStudyMetadata(studyUID);
      if (!metadata || !metadata.totalImages) {
        // No metadata or no totalImages field - can't verify
        return { isComplete: false, expected: 0, actual: 0 };
      }

      const expectedImages = metadata.totalImages;

      // Count actual images in filesystem
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(studyUID);
      const imagesDir = await studyDir.getDirectoryHandle('images');

      let actualImages = 0;

      // Count all image files across all series
      for await (const seriesEntry of imagesDir.values()) {
        if (seriesEntry.kind === 'directory') {
          const seriesDir = await imagesDir.getDirectoryHandle(
            seriesEntry.name
          );
          for await (const fileEntry of seriesDir.values()) {
            if (fileEntry.kind === 'file' && fileEntry.name.endsWith('.bin')) {
              actualImages++;
            }
          }
        }
      }

      return {
        isComplete: actualImages === expectedImages,
        expected: expectedImages,
        actual: actualImages,
      };
    } catch (error) {
      console.error(
        `[FileSystemStorage] Error checking integrity for ${studyUID}:`,
        error
      );
      return { isComplete: false, expected: 0, actual: 0 };
    }
  }

  /**
   * Get total size of all studies
   * @returns {Promise<number>} Total size in bytes
   */
  async getTotalSize() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return 0;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      let totalSize = 0;

      for await (const entry of studiesDir.values()) {
        if (entry.kind === 'directory') {
          try {
            const studyDir = await studiesDir.getDirectoryHandle(entry.name);

            // Calculate size of all images in study
            const imagesDir = await studyDir.getDirectoryHandle('images');
            for await (const seriesEntry of imagesDir.values()) {
              if (seriesEntry.kind === 'directory') {
                const seriesDir = await imagesDir.getDirectoryHandle(
                  seriesEntry.name
                );
                for await (const fileEntry of seriesDir.values()) {
                  if (fileEntry.kind === 'file') {
                    const file = await fileEntry.getFile();
                    totalSize += file.size;
                  }
                }
              }
            }
          } catch (error) {
            console.warn(
              `[FileSystemStorage] Could not get size for ${entry.name}`
            );
          }
        }
      }

      return totalSize;
    } catch (error) {
      console.error('[FileSystemStorage] Error calculating total size:', error);
      return 0;
    }
  }

  /**
   * Clear directory handle (revoke access)
   */
  async clearAccess() {
    this.directoryHandle = null;

    try {
      const db = await this.openDB();
      const tx = db.transaction('directory_handles', 'readwrite');
      const store = tx.objectStore('directory_handles');

      await new Promise((resolve, reject) => {
        const request = store.delete('main_directory');
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      // Silent fail
      console.error('[FileSystemStorage] Error clearing access:', error);
    }
  }

  /**
   * Save cache index to File System
   * @param {Object} indexData - Cache index data
   * @returns {Promise<boolean>} Success status
   */
  async saveCacheIndex(indexData) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache',
        { create: true }
      );

      const indexFile = await cacheDir.getFileHandle('cache_index.json', {
        create: true,
      });
      const writable = await indexFile.createWritable();
      await writable.write(
        new Blob([JSON.stringify(indexData, null, 2)], {
          type: 'application/json',
        })
      );
      await writable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving cache index:', error);
      return false;
    }
  }

  /**
   * Load cache index from File System
   * @returns {Promise<Object|null>} Cache index data or null if not found
   */
  async loadCacheIndex() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const indexFile = await cacheDir.getFileHandle('cache_index.json');
      const file = await indexFile.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error('[FileSystemStorage] Error loading cache index:', error);
      return null;
    }
  }

  /**
   * Save series data for a study
   * @param {string} studyUID - Study UID
   * @param {Object} seriesData - Series data to save
   * @returns {Promise<boolean>} Success status
   */
  async saveSeriesData(studyUID, seriesData) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const studyDir = await this.getOrCreateStudyDirectory(studyUID);
      const seriesFile = await studyDir.getFileHandle('series_data.json', {
        create: true,
      });
      const writable = await seriesFile.createWritable();
      await writable.write(
        new Blob([JSON.stringify(seriesData, null, 2)], {
          type: 'application/json',
        })
      );
      await writable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving series data:', error);
      return false;
    }
  }

  /**
   * Load series data for a study
   * @param {string} studyUID - Study UID
   * @returns {Promise<Object|null>} Series data or null if not found
   */
  async loadSeriesData(studyUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(studyUID);
      const seriesFile = await studyDir.getFileHandle('series_data.json');
      const file = await seriesFile.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error('[FileSystemStorage] Error loading series data:', error);
      return null;
    }
  }

  /**
   * Save instances map for a study
   * @param {string} studyUID - Study UID
   * @param {Object} instancesData - Instances map to save
   * @returns {Promise<boolean>} Success status
   */
  async saveInstancesMap(studyUID, instancesData) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const studyDir = await this.getOrCreateStudyDirectory(studyUID);
      const instancesFile = await studyDir.getFileHandle('instances_map.json', {
        create: true,
      });
      const writable = await instancesFile.createWritable();
      await writable.write(
        new Blob([JSON.stringify(instancesData, null, 2)], {
          type: 'application/json',
        })
      );
      await writable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving instances map:', error);
      return false;
    }
  }

  /**
   * Load instances map for a study
   * @param {string} studyUID - Study UID
   * @returns {Promise<Object|null>} Instances map or null if not found
   */
  async loadInstancesMap(studyUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(studyUID);
      const instancesFile = await studyDir.getFileHandle('instances_map.json');
      const file = await instancesFile.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error('[FileSystemStorage] Error loading instances map:', error);
      return null;
    }
  }

  /**
   * Save global series map (all studies)
   * @param {Object} seriesMap - Map of studyUID → [series]
   * @returns {Promise<boolean>} Success status
   */
  async saveSeriesMap(seriesMap) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache',
        { create: true }
      );

      const seriesMapFile = await cacheDir.getFileHandle('series_map.json', {
        create: true,
      });
      const writable = await seriesMapFile.createWritable();
      await writable.write(
        new Blob([JSON.stringify(seriesMap, null, 2)], {
          type: 'application/json',
        })
      );
      await writable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving series map:', error);
      return false;
    }
  }

  /**
   * Load global series map (all studies)
   * @returns {Promise<Object|null>} Series map or null if not found
   */
  async loadSeriesMap() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const seriesMapFile = await cacheDir.getFileHandle('series_map.json');
      const file = await seriesMapFile.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error('[FileSystemStorage] Error loading series map:', error);
      return null;
    }
  }

  /**
   * Save global instances map (all studies)
   * @param {Object} instancesMap - Map of seriesUID → [instances]
   * @returns {Promise<boolean>} Success status
   */
  async saveGlobalInstancesMap(instancesMap) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return false;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache',
        { create: true }
      );

      const instancesMapFile = await cacheDir.getFileHandle(
        'instances_map.json',
        { create: true }
      );
      const writable = await instancesMapFile.createWritable();
      await writable.write(
        new Blob([JSON.stringify(instancesMap, null, 2)], {
          type: 'application/json',
        })
      );
      await writable.close();

      return true;
    } catch (error) {
      console.error('[FileSystemStorage] Error saving instances map:', error);
      return false;
    }
  }

  /**
   * Load global instances map (all studies)
   * @returns {Promise<Object|null>} Instances map or null if not found
   */
  async loadGlobalInstancesMap() {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const instancesMapFile = await cacheDir.getFileHandle(
        'instances_map.json'
      );
      const file = await instancesMapFile.getFile();
      const text = await file.text();
      return JSON.parse(text);
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error('[FileSystemStorage] Error loading instances map:', error);
      return null;
    }
  }

  /**
   * Extract ZIP and save DICOM files to file system
   * @param {string} studyUID - Study UID
   * @param {Blob} zipBlob - ZIP file blob
   * @param {function} onProgress - Optional progress callback (filesExtracted, totalFiles)
   * @returns {Promise<object>} { savedCount, totalCount }
   */
  async extractZipAndSaveDicom(studyUID, zipBlob, onProgress) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        throw new Error(
          'No directory access. Call requestDirectoryAccess() first.'
        );
      }
    }

    try {
      // Load ZIP
      const zip = await JSZip.loadAsync(zipBlob);

      // Count total files (excluding directories)
      const allFiles = Object.keys(zip.files).filter(
        filename => !zip.files[filename].dir
      );
      const totalCount = allFiles.length;

      // Create study directory structure
      const studyDir = await this.getOrCreateStudyDirectory(studyUID);
      const dicomDir = await studyDir.getDirectoryHandle('dicom', {
        create: true,
      });

      let savedCount = 0;

      // Extract each file
      for (const filename of allFiles) {
        const zipEntry = zip.files[filename];

        try {
          // Get file as ArrayBuffer
          const arrayBuffer = await zipEntry.async('arraybuffer');

          // Extract SOP UID from filename (Orthanc uses pattern like "xxxxx.dcm")
          // For simplicity, use the filename as-is (Orthanc already includes meaningful names)
          const cleanFilename = filename.split('/').pop() || filename;

          // Save DICOM file
          const fileHandle = await dicomDir.getFileHandle(cleanFilename, {
            create: true,
          });
          const writable = await fileHandle.createWritable();
          await writable.write(arrayBuffer);
          await writable.close();

          savedCount++;

          // Call progress callback if provided
          if (onProgress) {
            onProgress(savedCount, totalCount);
          }

          if (savedCount % 10 === 0 || savedCount === totalCount) {
          }
        } catch (fileError) {
          console.error(
            `[FileSystemStorage] Error extracting file ${filename}:`,
            fileError
          );
          // Continue with other files
        }
      }

      return { savedCount, totalCount };
    } catch (error) {
      console.error(
        `[FileSystemStorage] Error extracting ZIP for study ${studyUID}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Load DICOM file from cache
   * @param {string} studyUID - Study UID
   * @param {string} filename - DICOM file name
   * @returns {Promise<ArrayBuffer|null>} DICOM file data or null if not found
   */
  async loadDicomFile(studyUID, filename) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return null;
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(studyUID);
      const dicomDir = await studyDir.getDirectoryHandle('dicom');

      const fileHandle = await dicomDir.getFileHandle(filename);
      const file = await fileHandle.getFile();
      return await file.arrayBuffer();
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error(
        `[FileSystemStorage] Error loading DICOM file ${filename}:`,
        error
      );
      return null;
    }
  }

  /**
   * List all DICOM files for a study
   * @param {string} studyUID - Study UID
   * @returns {Promise<Array<string>>} Array of DICOM filenames
   */
  async listDicomFiles(studyUID) {
    if (!this.directoryHandle) {
      const restored = await this.restoreDirectoryHandle();
      if (!restored) {
        return [];
      }
    }

    try {
      const cacheDir = await this.directoryHandle.getDirectoryHandle(
        'azviewer_cache'
      );
      const studiesDir = await cacheDir.getDirectoryHandle('studies');
      const studyDir = await studiesDir.getDirectoryHandle(studyUID);
      const dicomDir = await studyDir.getDirectoryHandle('dicom');

      const files = [];
      for await (const entry of dicomDir.values()) {
        if (entry.kind === 'file') {
          files.push(entry.name);
        }
      }

      return files;
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return [];
      }
      console.error(
        `[FileSystemStorage] Error listing DICOM files for ${studyUID}:`,
        error
      );
      return [];
    }
  }
}

// Export singleton instance
const fileSystemStorage = new FileSystemStorage();

export default fileSystemStorage;
